// Opening a note from the Home Tasks view puts the cursor at the end of the clicked task.
//
// The task id travels in the navigation state. The editor fills in asynchronously (and may then
// restore the note's collapsed sections), so the cursor is placed once the task is there and kept
// there for a short while — until the user clicks, types or touches the page themselves.

/** Navigation-state key for the task to focus (see TaskListComponent.openRow). */
export const NOTE_FOCUS_TASK_STATE_KEY = 'focusTaskId';

export function readFocusTaskId(navigationState: unknown): string | null {
  const value = (navigationState as Record<string, unknown> | null | undefined)?.[NOTE_FOCUS_TASK_STATE_KEY];
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Calls `focus` until it succeeds, then keeps it applied for `settleMs`; stops early on the user's
 * own input. Returns a function that stops it. */
export function focusTaskWhenReady(
  focus: () => boolean,
  options: { timeoutMs?: number; settleMs?: number; target?: EventTarget } = {},
): () => void {
  const timeoutMs = options.timeoutMs ?? 8000;
  const settleMs = options.settleMs ?? 1500;
  const target = options.target ?? (typeof document !== 'undefined' ? document : null);
  const startedAt = Date.now();
  let focusedAt: number | null = null;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const userEvents = ['mousedown', 'keydown', 'touchstart', 'wheel'];
  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    for (const type of userEvents) target?.removeEventListener(type, stop, true);
  };
  // Only after the first focus: until then the page is still loading and nothing would be lost.
  const listen = () => {
    for (const type of userEvents) target?.addEventListener(type, stop, { capture: true, passive: true });
  };

  const tick = () => {
    if (stopped) return;
    const now = Date.now();
    let ok = false;
    try {
      ok = focus();
    } catch {
      ok = false;
    }
    if (ok && focusedAt === null) {
      focusedAt = now;
      listen();
    }
    const done = focusedAt !== null ? now - focusedAt >= settleMs : now - startedAt >= timeoutMs;
    if (done) return stop();
    timer = setTimeout(tick, focusedAt === null ? 50 : 150);
  };

  timer = setTimeout(tick, 0);
  return stop;
}
